/**
 * solutionFinderProvenance — captures the active narrative bridges
 * (constraint, navigation-condition, industry-KPI) at save time so
 * the saved scenario / business case carries a permanent audit
 * trail of *why* it was modelled.
 *
 * The shape returned is what gets persisted to MongoDB under the
 * scenario's `provenance` field. Mirrors the dismiss-state hash
 * pattern from SolutionFinderContextStack so both surfaces speak
 * the same vocabulary.
 *
 *   {
 *     constraint?:   { stage, label, leak, recovery, attenuated },
 *     nav_condition?:{ focus, gap, label },
 *     industry_kpi?: { kpi_id, kpi_name },
 *     pe_benchmarking?: { condition, kpi_id, kpi_name },
 *     recorded_at: ISO string
 *   }
 *
 * Returns null when no bridges are active (most direct-navigation
 * saves) — the backend treats null as "no provenance" so older
 * records keep working.
 */

import { getConstraintContext } from './constraintContext';

function readUrlParams() {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

export function readActiveProvenance({ kpis } = {}) {
  const sp = readUrlParams();
  const out = {};

  // ─── Mission Control / Impact Modelling constraint ────────────
  // Reads URL first (fresh arrival) then falls back to the
  // session-stored constraint helper so saves later in the flow
  // still carry the framing.
  const urlLabel = sp.get('constraint_label');
  const urlLeak = parseInt(sp.get('leak'), 10);
  if (urlLabel && Number.isFinite(urlLeak)) {
    const recovery = parseInt(sp.get('recovery'), 10);
    out.constraint = {
      stage: sp.get('constraint_stage'),
      label: urlLabel,
      leak: urlLeak,
      recovery: Number.isFinite(recovery) ? recovery : null,
      attenuated: sp.get('attenuated') === '1',
    };
  } else {
    const stored = getConstraintContext();
    if (stored && stored.label && Number.isFinite(stored.leak)) {
      out.constraint = stored;
    }
  }

  // ─── Org Benchmarks navigation-condition delta ────────────────
  const navLabel = sp.get('label');
  const navFocus = sp.get('focus');
  if (navLabel && navFocus) {
    const gap = sp.get('gap');
    out.nav_condition = {
      focus: navFocus,
      gap: gap ? Number(gap) : null,
      label: navLabel,
    };
  }

  // ─── PE Benchmarking workforce-condition gap (?pe_condition=…) ──
  // Same ?kpi= deep-link as Industry Benchmarks, but flagged with a
  // pe_condition marker so it stamps as its own source rather than
  // masquerading as an Industry Benchmark gap.
  const kpiParam = sp.get('kpi');
  const peCondition = sp.get('pe_condition');
  const alignGap = sp.get('align_gap');
  if (peCondition) {
    const match = (kpis || []).find((k) => k.id === kpiParam);
    out.pe_benchmarking = {
      condition: peCondition,
      kpi_id: kpiParam || null,
      kpi_name: match?.name || kpiParam || null,
    };
  } else if (alignGap) {
    // ─── KPI Alignment transfer gap (?align_gap=1) ──────────────
    // Arrived from the Correlation page's KPI Alignment tab. Stamp
    // the expected-vs-observed influence so the Business Case Library
    // becomes an audit trail of which transfer gaps a leader modelled.
    const match = (kpis || []).find((k) => k.id === kpiParam);
    const expected = parseInt(sp.get('align_expected'), 10);
    const observed = parseInt(sp.get('align_observed'), 10);
    out.kpi_alignment = {
      measure_label: sp.get('align_label') || null,
      kpi_id: kpiParam || null,
      kpi_name: match?.name || sp.get('align_kpi_label') || kpiParam || null,
      expected: Number.isFinite(expected) ? expected : null,
      observed: Number.isFinite(observed) ? observed : null,
      gap: Number.isFinite(expected) && Number.isFinite(observed) ? expected - observed : null,
    };
  } else if (kpiParam) {
    // ─── Industry Benchmarks KPI gap ────────────────────────────
    const match = (kpis || []).find((k) => k.id === kpiParam);
    out.industry_kpi = {
      kpi_id: kpiParam,
      kpi_name: match?.name || kpiParam,
    };
  }

  if (!out.constraint && !out.nav_condition && !out.industry_kpi && !out.pe_benchmarking && !out.kpi_alignment) {
    return null;
  }
  out.recorded_at = new Date().toISOString();
  return out;
}
